import React, { useEffect } from 'react';
import { useNotificationStore, Notification, NotificationType } from '../../../stores/notificationStore';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const NotificationItem: React.FC<{ notification: Notification }> = ({ notification }) => {
  const removeNotification = useNotificationStore((state) => state.removeNotification);
  const [progress, setProgress] = React.useState(100);

  useEffect(() => {
    if (!notification.duration || notification.duration <= 0) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        const elapsed = Date.now() - notification.createdAt;
        const remaining = Math.max(0, 100 - (elapsed / (notification.duration ?? 5000)) * 100);
        return remaining;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [notification]);

  const getIcon = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-600" />;
    }
  };

  const getStyles = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
    }
  };

  const getProgressColor = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'info':
        return 'bg-blue-500';
    }
  };

  return (
    <div
      className={`notification-item ${getStyles(notification.type)} border rounded-lg shadow-lg p-4 min-w-[320px] max-w-md mb-3 relative overflow-hidden`}
      style={{
        animation: 'slideInRight 0.3s ease-out',
      }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">{getIcon(notification.type)}</div>

        <div className="flex-1 min-w-0">
          {notification.title && (
            <h4 className="font-semibold text-gray-900 mb-1">{notification.title}</h4>
          )}
          <p className="text-sm text-gray-700 break-words">{notification.message}</p>
        </div>

        <button
          onClick={() => removeNotification(notification.id)}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {notification.duration && notification.duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
          <div
            className={`h-full transition-all duration-50 ${getProgressColor(notification.type)}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

const NotificationContainer: React.FC = () => {
  const notifications = useNotificationStore((state) => state.notifications);

  if (notifications.length === 0) return null;

  return (
    <>
      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }

          @keyframes fadeOut {
            from {
              opacity: 1;
            }
            to {
              opacity: 0;
              transform: translateX(100%);
            }
          }

          .notification-item {
            transition: all 0.3s ease-out;
          }
        `}
      </style>

      <div className="fixed top-4 right-4 z-50 pointer-events-none">
        <div className="pointer-events-auto">
          {notifications.map((notification) => (
            <NotificationItem key={notification.id} notification={notification} />
          ))}
        </div>
      </div>
    </>
  );
};

export default NotificationContainer;
